import React, {useEffect, useState} from 'react'
import {Layer, Line, Rect, Shape} from 'react-konva'
import {ElementShapeType} from '../../../entities/project'
import {useStateWithRef} from '../../../hooks/useStateWithRef'
import type {KonvaNodeEvents} from 'react-konva'
import {Node as NodeRef, NodeConfig} from 'konva/lib/Node'
import {getRelativePointerPosition} from '../util'

export function PolygonToolLayer({
  onElementDrawn,
}: {
  onElementDrawn: (shape: {type: ElementShapeType; props: NodeConfig}) => void
}) {
  const [firstPoint, setFirstPoint, firstPointRef] = useStateWithRef<{
    x: number
    y: number
  } | null>(null)
  const [currentPoint, setCurrentPoint, currentPointRef] = useStateWithRef<{
    x: number
    y: number
  } | null>(null)
  const [pointsList, setPointsList, pointsListRef] = useStateWithRef<number[]>([])
  const [dimensions, setDimensions, dimensionsRef] = useStateWithRef<number[]>([])

  const layerRef = React.useRef<NodeRef>(null)

  useEffect(() => {
    const onClick: KonvaNodeEvents['onClick'] = (e) => {
      const {x, y} = getRelativePointerPosition(e.target)
      if (!firstPointRef.current) {
        setFirstPoint({x, y})
      } else {
        let xDif = pointsListRef.current[pointsListRef.current.length - 2] - x
        let yDif = pointsListRef.current[pointsListRef.current.length - 1] - y
        let side = xDif * xDif + yDif * yDif
        setDimensions((current) => [...current, side])
      }

      setPointsList((current) => [...current, x, y])

      //console.log(`mouse down: (${x}, ${y})`)
    }

    const onMouseMove: KonvaNodeEvents['onMouseMove'] = (e) => {
      if (!firstPointRef.current) {
        return
      }

      const {x: mouseX, y: mouseY} = getRelativePointerPosition(e.target)

      setCurrentPoint({
        x: mouseX,
        y: mouseY,
      })

      //console.log(`mouse move: ( ${mouseX}, ${mouseY})`)
    }

    const onDblClick: KonvaNodeEvents['onDblClick'] = (e) => {
      setPointsList((current) => {
        const newPoints = current.slice(0, current.length - 4)
        return newPoints
      })

      let xDif = pointsListRef.current[pointsListRef.current.length - 2] - pointsListRef.current[0]
      let yDif = pointsListRef.current[pointsListRef.current.length - 1] - pointsListRef.current[1]
      let side = xDif * xDif + yDif * yDif
      setDimensions((current) => [...current, side])
      onElementDrawn({
        type: 'polygon',
        props: {
          points: pointsListRef.current,
          closed: true,
          //stroke: 'black',
          //strokeWidth: 5,
        },
      })
      setFirstPoint(null)
      setCurrentPoint(null)
      setPointsList(null)
    }

    const stage = layerRef.current.getStage()

    stage.on('click', onClick)
    stage.on('mousemove', onMouseMove)
    stage.on('dblclick', onDblClick)

    return () => {
      if (stage) {
        stage.off('click', onClick)
        stage.off('mousemove', onMouseMove)
        stage.off('dblclick', onDblClick)
      }
    }
  }, [onElementDrawn])

  return (
    <Layer ref={layerRef as any} opacity={0.7}>
      {firstPoint && pointsList.length > 1 && (
        <Line strokeScaleEnabled={false} stroke="blue" points={pointsList} />
      )}
      {firstPoint && currentPoint && (
        <Line
          stroke="blue"
          strokeScaleEnabled={false}
          points={[
            pointsList[pointsList.length - 2],
            pointsList[pointsList.length - 1],
            currentPoint.x,
            currentPoint.y,
          ]}
        />
      )}
    </Layer>
  )
}
